import { useState, useEffect, useCallback } from 'react';
import DashboardLayout from '@/components/feature/DashboardLayout';
import { supabase } from '@/lib/supabase';
import CampoEndereco from '@/components/base/CampoEndereco';
import {
  apenasDigitos,
  limparDocumento,
  LIMITE_TEXTO,
  mascaraDocumento,
  mascaraTelefone,
  MENSAGEM,
  validarDocumento,
  validarEmail,
  validarTelefone,
} from '@/lib/mascaras';
import { useAuth } from '@/hooks/useAuth';
import { type Prestador } from '@/lib/prestadores';
import { classesDaCategoria, useCatalogo, type CategoriaServico } from '@/lib/catalogo';

function formatDoc(tipo: string, doc: string) {
  return mascaraDocumento(doc, tipo === 'pj' ? 'pj' : 'pf');
}

/** Chave de busca: ignora pontuação, preserva a letra do CNPJ alfanumérico. */
const chaveBusca = (valor: string) => valor.toUpperCase().replace(/[^0-9A-Z]/g, '');

/**
 * Categorias de serviço do catálogo (ADR-0011).
 *
 * Nome, ícone e cor saem de `categorias_servico`, no banco. Antes viviam em
 * três mapas no código — rótulo no mock, cor no mock, ícone aqui —, e uma
 * categoria nova exigia mexer nos três e publicar o portal.
 */
function useCategoriasServico() {
  const { itens } = useCatalogo<CategoriaServico>('categorias_servico');

  const achar = (id: string) => itens.find((c) => c.id === id);

  return {
    categorias: itens,
    nome: (id: string) => achar(id)?.nome || id,
    icone: (id: string) => achar(id)?.icone || 'ri-checkbox-circle-line',
    classes: (id: string) => classesDaCategoria(achar(id)?.cor),
  };
}

function PrestadorFormModal({
  prestador,
  onSave,
  onClose,
}: {
  prestador: Prestador | null;
  onSave: (data: any) => void;
  onClose: () => void;
}) {
  const enderecoPadrao = {
    cep: '',
    logradouro: '',
    numero: '',
    complemento: '',
    bairro: '',
    cidade: '',
    estado: '',
  };

  const [form, setForm] = useState({
    nome: prestador?.nome || '',
    tipo: prestador?.tipo || 'pj',
    documento: prestador?.documento || '',
    email: prestador?.email || '',
    telefone: prestador?.telefone || '',
    ativo: prestador?.ativo ?? true,
    observacoes: prestador?.observacoes || '',
    servicos: Array.isArray(prestador?.servicos) ? prestador.servicos : [],
    endereco: prestador?.endereco || enderecoPadrao,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const servico = useCategoriasServico();

  // A ordem importa: e a ordem visual do formulario, e o submit leva o foco
  // para o primeiro campo invalido desta lista.
  const CAMPOS_NA_ORDEM = ['nome', 'documento', 'email', 'telefone', 'servicos'] as const;

  const validate = (): Record<string, string> => {
    const errs: Record<string, string> = {};
    if (!form.nome.trim()) errs.nome = 'Nome é obrigatório';
    if (!form.documento.trim()) errs.documento = 'Documento é obrigatório';
    else if (!validarDocumento(form.documento, form.tipo as 'pf' | 'pj'))
      errs.documento = form.tipo === 'pj' ? MENSAGEM.cnpj : MENSAGEM.cpf;
    if (!form.email.trim()) errs.email = 'E-mail é obrigatório';
    else if (!validarEmail(form.email)) errs.email = MENSAGEM.email;
    if (!form.telefone.trim()) errs.telefone = 'Telefone é obrigatório';
    else if (!validarTelefone(form.telefone)) errs.telefone = MENSAGEM.telefone;
    if (form.servicos.length === 0) errs.servicos = 'Selecione ao menos um serviço';
    setErrors(errs);
    return errs;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const errs = validate();

    // Recusar em silencio e o pior desfecho possivel: o formulario e mais alto
    // que a tela, e a marca vermelha embaixo do campo pode estar fora do campo
    // de visao de quem acabou de clicar em Salvar. O resumo aparece junto do
    // botao, e o foco vai para o primeiro campo invalido — que o navegador
    // rola para dentro da area visivel por conta propria.
    const primeiro = CAMPOS_NA_ORDEM.find((campo) => errs[campo]);
    if (primeiro) {
      document.getElementById(`prestador-${primeiro}`)?.focus();
      return;
    }

    onSave({
      nome: form.nome.trim(),
      tipo: form.tipo,
      documento: limparDocumento(form.documento, form.tipo as 'pf' | 'pj'),
      email: form.email.trim(),
      telefone: apenasDigitos(form.telefone),
      ativo: form.ativo,
      observacoes: form.observacoes.trim(),
      servicos: form.servicos,
      endereco: Object.values(form.endereco).every((v) => !v) ? null : form.endereco,
    });
  };

  const toggleServico = (svc: string) => {
    setForm((prev) => ({
      ...prev,
      servicos: prev.servicos.includes(svc)
        ? prev.servicos.filter((s) => s !== svc)
        : [...prev.servicos, svc],
    }));
  };

  const updateEndereco = (field: string, value: string) => {
    setForm((prev) => ({
      ...prev,
      endereco: { ...prev.endereco, [field]: value },
    }));
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-[4vh] px-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose}></div>
      <div className="relative bg-background-50 rounded-lg border border-background-200/70 w-full max-w-4xl shadow-lg max-h-[92vh] overflow-y-auto">
        <div className="px-5 py-4 border-b border-background-200/70 flex items-center justify-between sticky top-0 bg-background-50 rounded-t-lg z-10">
          <h3 className="text-sm font-semibold text-foreground-900">
            {prestador ? 'Editar Prestador' : 'Novo Prestador'}
          </h3>
          <button
            onClick={onClose}
            className="w-7 h-7 rounded-md flex items-center justify-center text-foreground-500 hover:bg-background-100 cursor-pointer"
          >
            <i className="ri-close-line"></i>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 flex flex-col gap-5">
          {/* Tipo */}
          <div>
            <label className="block text-sm font-medium text-foreground-700 mb-2">Tipo</label>
            <div className="flex items-center gap-1 bg-background-100 rounded-lg p-1 w-fit">
              <button
                type="button"
                onClick={() => setForm({ ...form, tipo: 'pj' })}
                className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors whitespace-nowrap ${form.tipo === 'pj' ? 'bg-background-50 text-foreground-900' : 'text-foreground-500 hover:text-foreground-700'}`}
              >
                <i className="ri-building-2-line mr-1.5"></i>Pessoa Jurídica
              </button>
              <button
                type="button"
                onClick={() => setForm({ ...form, tipo: 'pf' })}
                className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors whitespace-nowrap ${form.tipo === 'pf' ? 'bg-background-50 text-foreground-900' : 'text-foreground-500 hover:text-foreground-700'}`}
              >
                <i className="ri-user-3-line mr-1.5"></i>Pessoa Física
              </button>
            </div>
          </div>

          {/* Nome + Documento */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="sm:col-span-2">
              <label
                htmlFor="prestador-nome"
                className="block text-sm font-medium text-foreground-700 mb-1.5"
              >
                Nome / Razão Social *
              </label>
              <input
                id="prestador-nome"
                aria-invalid={!!errors.nome}
                aria-describedby={errors.nome ? 'erro-prestador-nome' : undefined}
                type="text"
                value={form.nome}
                maxLength={LIMITE_TEXTO}
                onChange={(e) => setForm({ ...form, nome: e.target.value })}
                placeholder={
                  form.tipo === 'pj' ? 'Ex: ElevaTech Manutenção Ltda' : 'Ex: João Batista da Silva'
                }
                className={`w-full px-3 py-2.5 rounded-md border text-sm ${errors.nome ? 'border-red-300 bg-red-50' : 'border-background-300/60 bg-background-50'}`}
              />
              {errors.nome && (
                <p id="erro-prestador-nome" className="text-xs text-red-600 mt-1">
                  {errors.nome}
                </p>
              )}
            </div>
            <div>
              <label
                htmlFor="prestador-documento"
                className="block text-sm font-medium text-foreground-700 mb-1.5"
              >
                {form.tipo === 'pj' ? 'CNPJ' : 'CPF'} *
              </label>
              <input
                id="prestador-documento"
                aria-invalid={!!errors.documento}
                aria-describedby={errors.documento ? 'erro-prestador-documento' : undefined}
                type="text"
                value={form.documento}
                onChange={(e) =>
                  setForm({
                    ...form,
                    documento: mascaraDocumento(e.target.value, form.tipo as 'pf' | 'pj'),
                  })
                }
                inputMode={form.tipo === 'pj' ? undefined : 'numeric'}
                autoCapitalize={form.tipo === 'pj' ? 'characters' : undefined}
                maxLength={18}
                placeholder={form.tipo === 'pj' ? '00.000.000/0000-00' : '000.000.000-00'}
                className={`w-full px-3 py-2.5 rounded-md border text-sm ${errors.documento ? 'border-red-300 bg-red-50' : 'border-background-300/60 bg-background-50'}`}
              />
              {errors.documento && (
                <p id="erro-prestador-documento" className="text-xs text-red-600 mt-1">
                  {errors.documento}
                </p>
              )}
            </div>
          </div>

          {/* Email + Telefone */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label
                htmlFor="prestador-email"
                className="block text-sm font-medium text-foreground-700 mb-1.5"
              >
                E-mail *
              </label>
              <input
                id="prestador-email"
                aria-invalid={!!errors.email}
                aria-describedby={errors.email ? 'erro-prestador-email' : undefined}
                type="email"
                value={form.email}
                maxLength={LIMITE_TEXTO}
                onChange={(e) => setForm({ ...form, email: e.target.value.trim() })}
                placeholder="contato@empresa.com.br"
                className={`w-full px-3 py-2.5 rounded-md border text-sm ${errors.email ? 'border-red-300 bg-red-50' : 'border-background-300/60 bg-background-50'}`}
              />
              {errors.email && (
                <p id="erro-prestador-email" className="text-xs text-red-600 mt-1">
                  {errors.email}
                </p>
              )}
            </div>
            <div>
              <label
                htmlFor="prestador-telefone"
                className="block text-sm font-medium text-foreground-700 mb-1.5"
              >
                Telefone *
              </label>
              <input
                id="prestador-telefone"
                aria-invalid={!!errors.telefone}
                aria-describedby={errors.telefone ? 'erro-prestador-telefone' : undefined}
                type="text"
                value={form.telefone}
                onChange={(e) => setForm({ ...form, telefone: mascaraTelefone(e.target.value) })}
                inputMode="numeric"
                maxLength={15}
                placeholder="(11) 3456-7890"
                className={`w-full px-3 py-2.5 rounded-md border text-sm ${errors.telefone ? 'border-red-300 bg-red-50' : 'border-background-300/60 bg-background-50'}`}
              />
              {errors.telefone && (
                <p id="erro-prestador-telefone" className="text-xs text-red-600 mt-1">
                  {errors.telefone}
                </p>
              )}
            </div>
          </div>

          {/* Endereço */}
          <div>
            <div className="mb-3">
              <p className="block text-sm font-medium text-foreground-700 mb-2">Endereço</p>
              <CampoEndereco
                valor={{
                  cep: form.endereco.cep ?? '',
                  logradouro: form.endereco.logradouro ?? '',
                  numero: form.endereco.numero ?? '',
                  complemento: form.endereco.complemento ?? '',
                  bairro: form.endereco.bairro ?? '',
                  cidade: form.endereco.cidade ?? '',
                  estado: form.endereco.estado ?? '',
                }}
                onChange={(endereco) =>
                  setForm((prev) => ({
                    ...prev,
                    endereco: {
                      cep: endereco.cep,
                      logradouro: endereco.logradouro,
                      numero: endereco.numero,
                      complemento: endereco.complemento,
                      bairro: endereco.bairro,
                      cidade: endereco.cidade,
                      estado: endereco.estado,
                    },
                  }))
                }
                idPrefixo="prestador"
              />
            </div>
          </div>

          {/* Serviços */}
          <div>
            <label className="block text-sm font-medium text-foreground-700 mb-2 flex items-center gap-1.5">
              <div className="w-4 h-4 flex items-center justify-center text-foreground-400">
                <i className="ri-tools-line text-xs"></i>
              </div>
              Serviços *
            </label>
            {errors.servicos && <p className="text-xs text-red-600 mb-2">{errors.servicos}</p>}
            {/* `auto-fit` com largura minima, e nao um numero fixo de colunas:
                os rotulos vem do catalogo em banco e mudam de tamanho sem
                deploy. Com 4 colunas fixas, "Manutencao de Elevadores" nao
                cabia na celula e, por causa do `whitespace-nowrap`, vazava por
                cima do vizinho em vez de quebrar. */}
            <div
              id="prestador-servicos"
              tabIndex={-1}
              className="grid grid-cols-[repeat(auto-fit,minmax(13rem,1fr))] gap-2 outline-none"
            >
              {servico.categorias.map(({ id: key, nome: label }) => {
                const selected = form.servicos.includes(key);
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => toggleServico(key)}
                    className={`flex items-center gap-2 px-3 py-2 rounded-md text-xs font-medium text-left border transition-colors cursor-pointer leading-snug ${selected ? servico.classes(key) : 'border-background-300/60 text-foreground-500 hover:bg-background-100'}`}
                  >
                    <div
                      className={`w-5 h-5 rounded flex items-center justify-center flex-shrink-0 ${selected ? '' : 'bg-background-100'}`}
                    >
                      <i className={`${servico.icone(key)} text-xs`}></i>
                    </div>
                    <span className="min-w-0 break-words">{label}</span>
                  </button>
                );
              })}
            </div>
            {form.servicos.length > 0 && (
              <p className="text-xs text-foreground-500 mt-2">
                {form.servicos.length} serviço(s) selecionado(s)
              </p>
            )}
          </div>

          {/* Status + Observações */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground-700 mb-2">Status</label>
              <div className="flex items-center gap-1 bg-background-100 rounded-lg p-1 w-fit">
                <button
                  type="button"
                  onClick={() => setForm({ ...form, ativo: true })}
                  className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors whitespace-nowrap ${form.ativo ? 'bg-green-50 text-green-700 border border-green-200' : 'text-foreground-500 hover:text-foreground-700'}`}
                >
                  <i className="ri-check-double-line mr-1"></i>Ativo
                </button>
                <button
                  type="button"
                  onClick={() => setForm({ ...form, ativo: false })}
                  className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors whitespace-nowrap ${!form.ativo ? 'bg-secondary-100 text-secondary-900 border border-secondary-200' : 'text-foreground-500 hover:text-foreground-700'}`}
                >
                  <i className="ri-close-circle-line mr-1"></i>Inativo
                </button>
              </div>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground-700 mb-1.5">
              Observações
            </label>
            <textarea
              value={form.observacoes}
              onChange={(e) => setForm({ ...form, observacoes: e.target.value })}
              maxLength={500}
              rows={2}
              className="w-full px-3 py-2.5 rounded-md border border-background-300/60 bg-background-50 text-sm resize-y"
              placeholder="Informações relevantes sobre o prestador..."
            />
            <p className="text-xs text-foreground-400 mt-1">
              {form.observacoes.length}/500 caracteres
            </p>
          </div>

          {/* Rodape fixo: as acoes e o resumo dos erros ficam sempre visiveis,
              qualquer que seja a altura da tela ou a posicao da rolagem. Antes
              o unico sinal de recusa era a marca vermelha embaixo do campo, que
              podia estar bem longe do botao que a pessoa acabou de clicar. */}
          <div className="sticky bottom-0 -mx-5 -mb-5 px-5 py-4 bg-background-50 border-t border-background-200/70 rounded-b-lg">
            {Object.keys(errors).length > 0 && (
              <div
                role="alert"
                className="mb-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700"
              >
                <p className="font-medium mb-1">
                  {Object.keys(errors).length === 1
                    ? 'Corrija 1 campo para salvar:'
                    : `Corrija ${Object.keys(errors).length} campos para salvar:`}
                </p>
                <ul className="list-disc pl-4 space-y-0.5">
                  {CAMPOS_NA_ORDEM.filter((campo) => errors[campo]).map((campo) => (
                    <li key={campo}>{errors[campo]}</li>
                  ))}
                </ul>
              </div>
            )}
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2.5 rounded-md border border-background-300/60 text-sm font-medium text-foreground-700 hover:bg-background-100 transition-colors whitespace-nowrap"
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap"
              >
                {prestador ? 'Salvar Alterações' : 'Cadastrar Prestador'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ==================== PÁGINA PRINCIPAL ==================== */
export default function PrestadoresPage() {
  const { isLoggedIn, effectiveAccountId, isSuperuser } = useAuth();
  const servico = useCategoriasServico();
  const [prestadores, setPrestadores] = useState<Prestador[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busca, setBusca] = useState('');
  const [filtroServico, setFiltroServico] = useState('');
  const [filtroStatus, setFiltroStatus] = useState<'todos' | 'ativo' | 'inativo'>('todos');
  const [condoList, setCondoList] = useState<{ id: string; nome: string }[]>([]);
  const [selectedCondoId, setSelectedCondoId] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);
  const [prestadorCondoMap, setPrestadorCondoMap] = useState<Record<string, Set<string>>>();
  const [detailTarget, setDetailTarget] = useState<Prestador | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editTarget, setEditTarget] = useState<Prestador | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Prestador | null>(null);
  const [toast, setToast] = useState<{ msg: string; type: 'success' | 'info' | 'error' } | null>(
    null,
  );

  const showToast = (msg: string, type: 'success' | 'info' | 'error' = 'info') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  const fetchPrestadores = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      let query = supabase.from('prestadores').select('*').order('nome', { ascending: true });

      if (effectiveAccountId) {
        query = query.eq('account_id', effectiveAccountId);
      }

      const { data, error: fetchError } = await query;

      if (fetchError) {
        setError(fetchError.message);
      } else {
        setPrestadores((data || []) as Prestador[]);
      }
    } catch (err: any) {
      setError(err.message || 'Erro ao carregar prestadores');
    } finally {
      setLoading(false);
    }
  }, [effectiveAccountId]);

  useEffect(() => {
    fetchPrestadores();
  }, [fetchPrestadores]);

  // Fetch condominios + build prestador-condominio mapping from ordens_servico
  useEffect(() => {
    Promise.all([
      supabase.from('condominios').select('id, nome').order('nome', { ascending: true }),
      supabase.from('ordens_servico').select('condominio_id, prestador_id'),
    ])
      .then(([condosRes, osRes]) => {
        if (!condosRes.error && condosRes.data) {
          setCondoList(condosRes.data);
        }
        if (!osRes.error && osRes.data) {
          const map: Record<string, Set<string>> = {};
          (osRes.data as any[]).forEach((os) => {
            if (os.condominio_id && os.prestador_id) {
              if (!map[os.condominio_id]) map[os.condominio_id] = new Set();
              map[os.condominio_id].add(os.prestador_id);
            }
          });
          setPrestadorCondoMap(map);
        }
      })
      .catch(() => {});
  }, []);

  const handleSave = async (formData: any) => {
    if (editTarget) {
      const { error: uErr } = await supabase
        .from('prestadores')
        .update(formData)
        .eq('id', editTarget.id);
      if (uErr) {
        showToast(`Erro ao atualizar: ${uErr.message}`, 'error');
        return;
      }
      showToast(`"${formData.nome}" atualizado com sucesso.`, 'success');
    } else {
      const insertData: any = { ...formData };
      if (effectiveAccountId) {
        insertData.account_id = effectiveAccountId;
      }
      const { error: iErr } = await supabase.from('prestadores').insert([insertData]);
      if (iErr) {
        showToast(`Erro ao cadastrar: ${iErr.message}`, 'error');
        return;
      }
      showToast(`"${formData.nome}" cadastrado com sucesso.`, 'success');
    }
    setShowForm(false);
    setEditTarget(null);
    fetchPrestadores();
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    const { error: dErr } = await supabase.from('prestadores').delete().eq('id', deleteTarget.id);
    if (dErr) {
      showToast(`Erro ao excluir: ${dErr.message}`, 'error');
      setDeleteTarget(null);
      return;
    }
    showToast(`"${deleteTarget.nome}" excluído.`, 'success');
    setDeleteTarget(null);
    fetchPrestadores();
  };

  if (!isLoggedIn) {
    return (
      <DashboardLayout>
        <div className="max-w-6xl flex flex-col items-center justify-center py-20">
          <div className="w-16 h-16 rounded-full bg-red-50 flex items-center justify-center mb-4">
            <i className="ri-lock-line text-red-600 text-2xl"></i>
          </div>
          <h2 className="text-lg font-bold text-foreground-900 mb-2">Acesso Restrito</h2>
          <p className="text-sm text-foreground-500 text-center max-w-md">
            Faça login para acessar a lista de prestadores de serviço.
          </p>
        </div>
      </DashboardLayout>
    );
  }

  if (loading) {
    return (
      <DashboardLayout>
        <div className="max-w-6xl flex items-center justify-center py-20">
          <div className="flex flex-col items-center gap-4">
            <div className="w-10 h-10 border-2 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
            <p className="text-sm text-foreground-500">Carregando prestadores...</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (error) {
    return (
      <DashboardLayout>
        <div className="max-w-6xl flex items-center justify-center py-20">
          <div className="flex flex-col items-center gap-4 text-center max-w-md">
            <div className="w-14 h-14 rounded-full bg-red-50 flex items-center justify-center">
              <i className="ri-error-warning-line text-red-500 text-2xl"></i>
            </div>
            <h2 className="text-lg font-semibold text-foreground-900">Erro ao carregar</h2>
            <p className="text-sm text-foreground-600">{error}</p>
            <button
              onClick={fetchPrestadores}
              className="px-4 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              Tentar novamente
            </button>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  const filtrados = prestadores.filter((p) => {
    const matchBusca =
      p.nome.toLowerCase().includes(busca.toLowerCase()) ||
      chaveBusca(p.documento).includes(chaveBusca(busca)) ||
      p.email.toLowerCase().includes(busca.toLowerCase()) ||
      (p.endereco?.cidade || '').toLowerCase().includes(busca.toLowerCase());
    const matchServico = filtroServico
      ? (Array.isArray(p.servicos) ? p.servicos : []).includes(filtroServico)
      : true;
    const matchStatus =
      filtroStatus === 'todos' ? true : filtroStatus === 'ativo' ? p.ativo : !p.ativo;
    const matchCondo =
      selectedCondoId && prestadorCondoMap
        ? (prestadorCondoMap[selectedCondoId]?.has(p.id) ?? false)
        : true;
    return matchBusca && matchServico && matchStatus && matchCondo;
  });

  const ativos = prestadores.filter((p) => p.ativo).length;
  const inativos = prestadores.filter((p) => !p.ativo).length;

  const servicosUnicos = [
    ...new Set(prestadores.flatMap((p) => (Array.isArray(p.servicos) ? p.servicos : []))),
  ].sort();

  return (
    <DashboardLayout>
      <div className="max-w-6xl">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-xl md:text-2xl font-bold text-foreground-900">
              Prestadores de Serviço
            </h1>
            <p className="text-sm text-foreground-500 mt-0.5">
              Gerencie empresas e profissionais terceirizados que atendem os condomínios.
            </p>
          </div>
          <button
            onClick={() => {
              setEditTarget(null);
              setShowForm(true);
            }}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap"
          >
            <i className="ri-add-line"></i>Novo Prestador
          </button>
        </div>

        <div className="flex flex-col gap-6">
          {/* Cards resumo */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {[
              {
                label: 'Total de prestadores',
                valor: prestadores.length,
                icone: 'ri-team-line',
                corIcon: 'text-primary-600',
                corBg: 'bg-primary-50',
              },
              {
                label: 'Ativos',
                valor: ativos,
                icone: 'ri-check-double-line',
                corIcon: 'text-green-600',
                corBg: 'bg-green-50',
              },
              {
                label: 'Inativos',
                valor: inativos,
                icone: 'ri-close-circle-line',
                corIcon: 'text-red-600',
                corBg: 'bg-red-50',
              },
            ].map((card) => (
              <div
                key={card.label}
                className="bg-background-50 border border-background-200/70 rounded-lg p-4"
              >
                <div className="flex items-center gap-2 mb-1">
                  <div
                    className={`w-8 h-8 rounded-lg ${card.corBg} flex items-center justify-center`}
                  >
                    <i className={`${card.icone} ${card.corIcon} text-sm`}></i>
                  </div>
                  <p className="text-xs font-medium text-foreground-500">{card.label}</p>
                </div>
                <p className="text-2xl font-bold text-foreground-900">{card.valor}</p>
              </div>
            ))}
          </div>

          {/* Filtros */}
          <div className="flex flex-col sm:flex-row gap-3 flex-wrap">
            <div className="relative flex-1 max-w-sm">
              <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                <i className="ri-search-line text-foreground-400 text-sm"></i>
              </div>
              <input
                type="text"
                placeholder="Buscar prestador, documento ou cidade..."
                value={busca}
                onChange={(e) => setBusca(e.target.value)}
                className="w-full pl-9 pr-3 py-2.5 rounded-md border border-background-300/60 bg-background-50 text-sm"
              />
            </div>
            <div className="relative">
              <button
                onClick={() => setFilterOpen(!filterOpen)}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-lg border transition-colors text-sm font-medium whitespace-nowrap ${
                  selectedCondoId
                    ? 'bg-primary-50 border-primary-300 text-primary-800'
                    : 'bg-background-50 border-background-300/60 text-foreground-700 hover:bg-background-100'
                }`}
              >
                <i
                  className={`text-base ${selectedCondoId ? 'ri-building-2-fill' : 'ri-building-2-line'}`}
                ></i>
                <span>
                  {selectedCondoId
                    ? condoList.find((c) => c.id === selectedCondoId)?.nome || ''
                    : 'Todos os condomínios'}
                </span>
                <i
                  className={`${filterOpen ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-xs transition-transform`}
                ></i>
              </button>

              {filterOpen && (
                <>
                  <div className="fixed inset-0 z-10" onClick={() => setFilterOpen(false)}></div>
                  <div className="absolute top-full left-0 mt-1 w-72 bg-background-50 border border-background-200/70 rounded-lg shadow-lg z-20 overflow-hidden">
                    <div className="max-h-64 overflow-y-auto py-1">
                      <button
                        onClick={() => {
                          setSelectedCondoId('');
                          setFilterOpen(false);
                        }}
                        className={`w-full text-left px-4 py-2.5 text-sm transition-colors flex items-center gap-2 ${
                          !selectedCondoId
                            ? 'bg-primary-50 text-primary-800 font-medium'
                            : 'text-foreground-700 hover:bg-background-100'
                        }`}
                      >
                        <i className="ri-global-line text-base"></i>
                        <span>Todos os condomínios</span>
                        {!selectedCondoId && (
                          <i className="ri-check-line ml-auto text-primary-600"></i>
                        )}
                      </button>
                      {condoList.map((condo) => (
                        <button
                          key={condo.id}
                          onClick={() => {
                            setSelectedCondoId(condo.id);
                            setFilterOpen(false);
                          }}
                          className={`w-full text-left px-4 py-2.5 text-sm transition-colors flex items-center gap-2 ${
                            selectedCondoId === condo.id
                              ? 'bg-primary-50 text-primary-800 font-medium'
                              : 'text-foreground-700 hover:bg-background-100'
                          }`}
                        >
                          <i className="ri-building-2-line text-base"></i>
                          <span>{condo.nome}</span>
                          {selectedCondoId === condo.id && (
                            <i className="ri-check-line ml-auto text-primary-600"></i>
                          )}
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>

            {selectedCondoId && (
              <button
                onClick={() => setSelectedCondoId('')}
                className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium text-foreground-500 hover:text-foreground-700 hover:bg-background-100 transition-colors whitespace-nowrap"
              >
                <i className="ri-close-line"></i>
                <span>Limpar filtro</span>
              </button>
            )}
            <select
              value={filtroServico}
              onChange={(e) => setFiltroServico(e.target.value)}
              className="px-3 py-2.5 rounded-md border border-background-300/60 bg-background-50 text-sm"
            >
              <option value="">Todos os serviços</option>
              {servicosUnicos.map((svc) => (
                <option key={svc} value={svc}>
                  {servico.nome(svc)}
                </option>
              ))}
            </select>
            <select
              value={filtroStatus}
              onChange={(e) => setFiltroStatus(e.target.value as typeof filtroStatus)}
              className="px-3 py-2.5 rounded-md border border-background-300/60 bg-background-50 text-sm"
            >
              <option value="todos">Todos os status</option>
              <option value="ativo">Ativo</option>
              <option value="inativo">Inativo</option>
            </select>
          </div>

          {/* Tabela */}
          <div className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-background-200/70 bg-background-100/50">
                    <th className="text-left text-xs font-semibold text-foreground-500 px-4 py-3 whitespace-nowrap">
                      Prestador
                    </th>
                    <th className="text-left text-xs font-semibold text-foreground-500 px-4 py-3 whitespace-nowrap hidden md:table-cell">
                      Tipo
                    </th>
                    <th className="text-left text-xs font-semibold text-foreground-500 px-4 py-3 whitespace-nowrap">
                      Serviços
                    </th>
                    <th className="text-left text-xs font-semibold text-foreground-500 px-4 py-3 whitespace-nowrap hidden lg:table-cell">
                      Contato
                    </th>
                    <th className="text-center text-xs font-semibold text-foreground-500 px-4 py-3 whitespace-nowrap">
                      Status
                    </th>
                    <th className="text-right text-xs font-semibold text-foreground-500 px-4 py-3 whitespace-nowrap"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-background-200/50">
                  {filtrados.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="px-4 py-12 text-center">
                        <div className="w-12 h-12 rounded-full bg-background-100 flex items-center justify-center mx-auto mb-3">
                          <i className="ri-team-line text-foreground-400 text-xl"></i>
                        </div>
                        <p className="text-sm text-foreground-500">
                          {prestadores.length === 0
                            ? 'Nenhum prestador cadastrado ainda.'
                            : 'Nenhum prestador encontrado com os filtros atuais.'}
                        </p>
                      </td>
                    </tr>
                  ) : (
                    filtrados.map((p) => (
                      <tr
                        key={p.id}
                        className={`hover:bg-background-100/30 transition-colors ${!p.ativo ? 'opacity-60' : ''}`}
                      >
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <div
                              className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${p.tipo === 'pj' ? 'bg-accent-50' : 'bg-secondary-50'}`}
                            >
                              <i
                                className={`${p.tipo === 'pj' ? 'ri-building-2-line' : 'ri-user-3-line'} ${p.tipo === 'pj' ? 'text-accent-600' : 'text-secondary-600'} text-sm`}
                              ></i>
                            </div>
                            <div className="min-w-0">
                              <p className="text-sm font-medium text-foreground-800 truncate">
                                {p.nome}
                              </p>
                              <p className="text-xs text-foreground-500 truncate">
                                {p.tipo === 'pj' ? 'CNPJ' : 'CPF'}: {formatDoc(p.tipo, p.documento)}
                              </p>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 hidden md:table-cell">
                          <span
                            className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium border ${
                              p.tipo === 'pj'
                                ? 'bg-accent-50 text-accent-700 border-accent-200'
                                : 'bg-secondary-50 text-secondary-700 border-secondary-200'
                            }`}
                          >
                            {p.tipo === 'pj' ? 'Pessoa Jurídica' : 'Pessoa Física'}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex flex-wrap gap-1">
                            {(Array.isArray(p.servicos) ? p.servicos : [])
                              .slice(0, 3)
                              .map((svc) => (
                                <span
                                  key={svc}
                                  className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium border ${servico.classes(svc)}`}
                                >
                                  {servico.nome(svc)}
                                </span>
                              ))}
                            {(Array.isArray(p.servicos) ? p.servicos.length : 0) > 3 && (
                              <span className="inline-block px-2 py-0.5 rounded-full text-xs font-medium bg-background-100 text-foreground-500 border border-background-200">
                                +{(Array.isArray(p.servicos) ? p.servicos.length : 0) - 3}
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3 hidden lg:table-cell">
                          <div className="min-w-0">
                            <p className="text-sm text-foreground-700 truncate flex items-center gap-1.5">
                              <div className="w-3.5 h-3.5 flex items-center justify-center text-foreground-400">
                                <i className="ri-mail-line text-xs"></i>
                              </div>
                              {p.email}
                            </p>
                            <p className="text-xs text-foreground-500 truncate flex items-center gap-1.5 mt-0.5">
                              <div className="w-3.5 h-3.5 flex items-center justify-center text-foreground-400">
                                <i className="ri-phone-line text-xs"></i>
                              </div>
                              {p.telefone}
                            </p>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span
                            className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-medium border ${
                              p.ativo
                                ? 'bg-green-50 text-green-700 border-green-200'
                                : 'bg-secondary-100 text-secondary-900 border-secondary-200'
                            }`}
                          >
                            {p.ativo ? 'Ativo' : 'Inativo'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex items-center justify-end gap-1">
                            <button
                              onClick={() => {
                                setEditTarget(p);
                                setShowForm(true);
                              }}
                              className="w-8 h-8 rounded-md flex items-center justify-center text-foreground-500 hover:bg-background-100 hover:text-foreground-800 transition-colors cursor-pointer"
                              title="Editar"
                            >
                              <i className="ri-pencil-line text-sm"></i>
                            </button>
                            <button
                              onClick={() => setDetailTarget(p)}
                              className="w-8 h-8 rounded-md flex items-center justify-center text-foreground-500 hover:bg-background-100 hover:text-primary-600 transition-colors cursor-pointer"
                              title="Ver detalhes"
                            >
                              <i className="ri-eye-line text-sm"></i>
                            </button>
                            <button
                              onClick={() => setDeleteTarget(p)}
                              className="w-8 h-8 rounded-md flex items-center justify-center text-foreground-500 hover:bg-background-100 hover:text-red-600 transition-colors cursor-pointer"
                              title="Excluir"
                            >
                              <i className="ri-delete-bin-line text-sm"></i>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {/* Modal de cadastro/edição */}
      {showForm && (
        <PrestadorFormModal
          prestador={editTarget}
          onSave={handleSave}
          onClose={() => {
            setShowForm(false);
            setEditTarget(null);
          }}
        />
      )}

      {/* Modal de detalhe */}
      {detailTarget && (
        <PrestadorDetailModal
          prestador={detailTarget}
          onEdit={() => {
            setEditTarget(detailTarget);
            setDetailTarget(null);
            setShowForm(true);
          }}
          onClose={() => setDetailTarget(null)}
          onDelete={() => {
            setDeleteTarget(detailTarget);
            setDetailTarget(null);
          }}
        />
      )}

      {/* Modal de confirmação de exclusão */}
      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={() => setDeleteTarget(null)}></div>
          <div className="relative bg-background-50 rounded-lg border border-background-200/70 p-6 w-full max-w-md mx-4 shadow-lg">
            <p className="text-sm font-semibold text-foreground-900 mb-2">Confirmar exclusão</p>
            <p className="text-sm text-foreground-600 mb-6">
              Tem certeza que deseja excluir <strong>{deleteTarget.nome}</strong>?
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setDeleteTarget(null)}
                className="px-4 py-2 rounded-md border border-background-300/60 text-sm font-medium text-foreground-700 hover:bg-background-100 transition-colors whitespace-nowrap"
              >
                Cancelar
              </button>
              <button
                onClick={handleDelete}
                className="px-4 py-2 rounded-md bg-red-600 text-white text-sm font-medium hover:bg-red-700 transition-colors whitespace-nowrap"
              >
                Excluir
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div className="fixed bottom-6 right-6 z-50">
          <div
            className={`px-4 py-3 rounded-lg text-sm font-medium shadow-lg flex items-center gap-2 ${toast.type === 'success' ? 'bg-green-50 text-green-800 border border-green-200' : toast.type === 'error' ? 'bg-red-50 text-red-800 border border-red-200' : 'bg-background-50 text-foreground-800 border border-background-200'}`}
          >
            <i
              className={`${toast.type === 'success' ? 'ri-checkbox-circle-line text-green-600' : toast.type === 'error' ? 'ri-close-circle-line text-red-600' : 'ri-information-line text-foreground-500'}`}
            ></i>
            {toast.msg}
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}

/* ==================== MODAL DE DETALHE ==================== */
function PrestadorDetailModal({
  prestador,
  onEdit,
  onClose,
  onDelete,
}: {
  prestador: Prestador;
  onEdit: () => void;
  onClose: () => void;
  onDelete: () => void;
}) {
  const servico = useCategoriasServico();
  const servicos = Array.isArray(prestador.servicos) ? prestador.servicos : [];

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-[6vh] px-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose}></div>
      <div className="relative bg-background-50 rounded-lg border border-background-200/70 w-full max-w-xl shadow-lg max-h-[88vh] overflow-y-auto">
        {/* Header */}
        <div className="px-5 py-4 border-b border-background-200/70 flex items-center justify-between sticky top-0 bg-background-50 rounded-t-lg z-10">
          <div className="flex items-center gap-3">
            <div
              className={`w-10 h-10 rounded-lg flex items-center justify-center ${prestador.tipo === 'pj' ? 'bg-accent-50' : 'bg-secondary-50'}`}
            >
              <i
                className={`${prestador.tipo === 'pj' ? 'ri-building-2-line' : 'ri-user-3-line'} ${prestador.tipo === 'pj' ? 'text-accent-600' : 'text-secondary-600'}`}
              ></i>
            </div>
            <div>
              <h3 className="text-sm font-semibold text-foreground-900">{prestador.nome}</h3>
              <p className="text-xs text-foreground-500">
                {prestador.tipo === 'pj' ? 'Pessoa Jurídica' : 'Pessoa Física'}
                <span
                  className={`ml-2 inline-block w-1.5 h-1.5 rounded-full ${prestador.ativo ? 'bg-green-500' : 'bg-foreground-400'}`}
                ></span>
                <span className="ml-1">{prestador.ativo ? 'Ativo' : 'Inativo'}</span>
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-7 h-7 rounded-md flex items-center justify-center text-foreground-500 hover:bg-background-100 cursor-pointer"
          >
            <i className="ri-close-line"></i>
          </button>
        </div>

        {/* Content */}
        <div className="p-5 flex flex-col gap-5">
          {/* Documento */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <p className="text-xs font-medium text-foreground-500 mb-1">
                {prestador.tipo === 'pj' ? 'CNPJ' : 'CPF'}
              </p>
              <p className="text-sm font-medium text-foreground-800">
                {formatDoc(prestador.tipo, prestador.documento)}
              </p>
            </div>
            <div>
              <p className="text-xs font-medium text-foreground-500 mb-1">Cadastro</p>
              <p className="text-sm font-medium text-foreground-800">
                {new Date(prestador.created_at).toLocaleDateString('pt-BR')}
              </p>
            </div>
          </div>

          {/* Contato */}
          <div className="bg-background-100/50 rounded-lg p-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-md bg-primary-50 flex items-center justify-center flex-shrink-0">
                <i className="ri-mail-line text-primary-600 text-xs"></i>
              </div>
              <div className="min-w-0">
                <p className="text-xs text-foreground-500">E-mail</p>
                <p className="text-sm text-foreground-800 truncate">{prestador.email}</p>
              </div>
            </div>
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-md bg-green-50 flex items-center justify-center flex-shrink-0">
                <i className="ri-phone-line text-green-600 text-xs"></i>
              </div>
              <div className="min-w-0">
                <p className="text-xs text-foreground-500">Telefone</p>
                <p className="text-sm text-foreground-800">{prestador.telefone}</p>
              </div>
            </div>
          </div>

          {/* Endereço */}
          {prestador.endereco && (
            <div>
              <p className="text-xs font-semibold text-foreground-700 mb-2 flex items-center gap-1.5">
                <div className="w-4 h-4 flex items-center justify-center text-foreground-400">
                  <i className="ri-map-pin-line text-xs"></i>
                </div>
                Endereço
              </p>
              <div className="bg-background-100/50 rounded-lg p-4">
                <p className="text-sm text-foreground-800">
                  {prestador.endereco.logradouro}, {prestador.endereco.numero}
                  {prestador.endereco.complemento ? ` — ${prestador.endereco.complemento}` : ''}
                </p>
                <p className="text-sm text-foreground-600 mt-0.5">
                  {prestador.endereco.bairro} — {prestador.endereco.cidade},{' '}
                  {prestador.endereco.estado}
                </p>
                <p className="text-xs text-foreground-500 mt-0.5">CEP: {prestador.endereco.cep}</p>
              </div>
            </div>
          )}

          {/* Serviços */}
          <div>
            <p className="text-xs font-semibold text-foreground-700 mb-2 flex items-center gap-1.5">
              <div className="w-4 h-4 flex items-center justify-center text-foreground-400">
                <i className="ri-tools-line text-xs"></i>
              </div>
              Serviços executados ({servicos.length})
            </p>
            <div className="flex flex-wrap gap-1.5">
              {servicos.map((svc) => (
                <span
                  key={svc}
                  className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium border ${servico.classes(svc)}`}
                >
                  <i className={`${servico.icone(svc)} text-xs`}></i>
                  {servico.nome(svc)}
                </span>
              ))}
            </div>
          </div>

          {/* Observações */}
          {prestador.observacoes && (
            <div className="bg-background-100/50 rounded-lg p-4">
              <p className="text-xs font-semibold text-foreground-700 mb-1 flex items-center gap-1.5">
                <div className="w-4 h-4 flex items-center justify-center text-foreground-400">
                  <i className="ri-sticky-note-line text-xs"></i>
                </div>
                Observações
              </p>
              <p className="text-sm text-foreground-600 leading-relaxed">{prestador.observacoes}</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-4 border-t border-background-200/70 flex justify-between sticky bottom-0 bg-background-50 rounded-b-lg">
          <button
            onClick={onDelete}
            className="px-3 py-2 rounded-md border border-red-200 text-sm text-red-600 hover:bg-red-50 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-delete-bin-line mr-1.5"></i>Excluir
          </button>
          <div className="flex gap-2">
            <button
              onClick={onEdit}
              className="px-4 py-2 rounded-md border border-background-300/60 text-sm font-medium text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-pencil-line mr-1.5"></i>Editar
            </button>
            <button
              onClick={onClose}
              className="px-4 py-2 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              Fechar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
